<template>
  <div>
    <base-tooltip-button id="trigger-button"
                         :variant="isSelectedLink ? 'rich-input-active' : 'rich-input'"
                         :tooltip-text="triggerTooltip"
                         @click="initInsertLink"
    >
      <component :is="triggerIcon" class="h-5 w-5" :class="isSelectedLink ? 'text-primary' : 'text-gray-dark'"/>
    </base-tooltip-button>

    <div v-if="showLinkForm"
         id="link-form"
         ref="richInputLink"
         class="absolute flex gap-1 rounded border border-solid bg-white p-1 border-gray z-50"
         :style="`left: ${dropdownPosition.left}px; top: ${dropdownPosition.top}px`"
    >
      <div class="flex flex-col">
        <base-input ref="input"
                    id="link-input"
                    variant="rich-input"
                    placeholder="Link URL (enter)..."
                    v-model="linkUrl"
                    class="w-48"
                    @keyup.enter="insertLink"
        />
        <base-input ref="linkTextInput"
                    id="link-text-input"
                    variant="rich-input"
                    placeholder="Link text (optional)"
                    v-model="linkText"
                    class="w-48 mt-1"
                    @keyup.enter="insertLink"
        />
      </div>

      <base-tooltip-button variant="transparent-success"
                           :tooltip-text="confirmButtonTooltip"
                           id="confirm-button"
                           @click="insertLink"
      >
        <vs-icon icon="done" class="text-success font-extrabold"/>
      </base-tooltip-button>

      <base-tooltip-button v-if="isSelectedLink"
                           variant="transparent-error"
                           tooltip-text="Remove link"
                           id="delete-button"
                           @click="resetLinkForm(true)"
      >
        <delete-icon class="h-4 w-4"/>
      </base-tooltip-button>
    </div>

  </div>
</template>

<script>
import BaseTooltipButton from '@/components/base-components/BaseTooltipButton.vue'
import BaseButton from '@/components/base-components/BaseButton.vue'
import LinkIcon from '@/assets/images/icons/LinkIcon.vue'
import DeleteIcon from '@/assets/images/icons/DeleteIcon.vue'
import { VARIABLE_REGEX_GLOBAL } from '@/constants/variable-refs.constants.js'
import BaseInput from '@/components/base-components/BaseInput.vue'
import includes from 'ramda/src/includes'
import isNil from 'ramda/src/isNil'
import path from 'ramda/src/path'

export default {
  name: 'RichInputLink',

  components: {
    BaseInput,
    BaseButton,
    BaseTooltipButton,
    DeleteIcon,
    LinkIcon
  },

  props: {
    triggerIcon: {
      type: String,
      default: 'link-icon'
    },
    placeholder: {
      type: String,
      default: 'Insert link (enter)...'
    },
    triggerTooltip: {
      type: String,
      default: 'Insert link'
    },
    cursorPosition: {
      type: Object,
      default: () => {}
    },
    textEditorId: {
      type: String,
      default: ''
    },
    formatting: {
      type: Object,
      default: () => {}
    },
    getSelectedTextEditor: {
      type: Function,
      default: () => (() => '')
    }
  },

  data() {
    return {
      linkUrl: '',
      linkText: '',
      showLinkForm: false,
      dropdownPosition: {
        top: 0,
        left: 0
      },
      isSelectedLink: false,
      originalLinkHref: null,
      isEditingVariableLink: false,
      initialUrlInputValue: null
    }
  },

  computed: {
    confirmButtonTooltip() {
      return this.isSelectedLink ? 'Update link' : 'Create link'
    },

    editor() {
      return this.$root.$el.querySelector(`#${this.textEditorId}`)
    }
  },

  methods: {
    insertLink() {
      let finalUrl = this.linkUrl
      if (this.isEditingVariableLink && this.linkUrl === this.initialUrlInputValue) {
        finalUrl = this.originalLinkHref
      }

      this.$emit('insert-link', {
        url: finalUrl,
        text: this.linkText,
        originalHref: this.originalLinkHref
      })
      this.resetLinkForm()
    },

    async onClick(e) {
      const targetLinkElement = e.target.closest('a')
      if (targetLinkElement) {
        const hrefAttribute = targetLinkElement.getAttribute('href')
        const innerText = targetLinkElement.innerText
        if (hrefAttribute) {
          await this.initInsertLink(e, hrefAttribute, innerText)
        }
        return
      }

      const targetId = path(['target', 'id'], e)
      const parentElement = path(['target', 'parentElement'], e)
      const parentButtonId = path(['id'], parentElement)

      const noCloseIds = ['link-input', 'link-text-input', 'link-form', 'trigger-button', 'confirm-button', 'delete-button']

      if (includes(targetId, noCloseIds) || includes(parentButtonId, noCloseIds)) {
        return
      }

      this.resetLinkForm()
    },

    resetAndShowForm() {
      this.showLinkForm = true
      this.isSelectedLink = false
      this.isEditingVariableLink = false
      this.linkText = ''
      this.linkUrl = ''
      this.originalLinkHref = null
    },

    prepareEditMode(href, innerText, selectedEditorText) {
      this.originalLinkHref = href
      this.isSelectedLink = true
      this.linkText = selectedEditorText || innerText || ''

      this.linkUrl = href
      this.isEditingVariableLink = VARIABLE_REGEX_GLOBAL.test(href)
    },

    prepareCreateMode(selectedEditorText) {
      this.linkUrl = ''
      this.linkText = selectedEditorText || ''
    },

    async setDropdownPositionAndFocus(event, isEditorLinkClicked) {
      if (isEditorLinkClicked) {
        await this.setDropdownPositionByClickInTextArea(event)
      } else {
        await this.setDropdownPositionByClickOnButton(event)
      }

      await this.$nextTick()
      const inputElement = path(['input'], this.$refs)
      if (inputElement && typeof inputElement.focus === 'function') {
        inputElement.focus()
      }
    },

    async initInsertLink(event, hrefFromEditorClick, innerTextFromEditorClick) {
      this.resetAndShowForm()
      const selectedEditorText = this.getSelectedTextEditor()

      const isEditorLinkClicked = !!hrefFromEditorClick
      const isEditingViaToolbar = !isEditorLinkClicked && !isNil(this.formatting.link) && this.formatting.link !== ''

      if (isEditorLinkClicked) {
        this.prepareEditMode(hrefFromEditorClick, innerTextFromEditorClick, selectedEditorText)
      } else if (isEditingViaToolbar) {
        this.prepareEditMode(this.formatting.link, selectedEditorText, selectedEditorText)
      } else {
        this.prepareCreateMode(selectedEditorText)
      }

      this.initialUrlInputValue = this.linkUrl
      await this.setDropdownPositionAndFocus(event, isEditorLinkClicked)
    },

    resetLinkForm(removeLink) {
      if (removeLink && this.originalLinkHref) {
        this.$emit('remove-link', this.originalLinkHref)
      }

      this.showLinkForm = false
      this.isSelectedLink = false
      this.linkUrl = ''
      this.linkText = ''
      this.originalLinkHref = null
      this.isEditingVariableLink = false
      this.initialUrlInputValue = null
    },

    async setDropdownPositionByClickOnButton(e) {
      await this.$nextTick()
      const dropdown = this.$refs.richInputLink
      this.editor.appendChild(dropdown)
      this.dropdownPosition.top = this.cursorPosition.top + this.cursorPosition.height
      if (dropdown.clientWidth < this.cursorPosition.left) {
        this.dropdownPosition.left = this.cursorPosition.left - dropdown.clientWidth + e.target.offsetWidth
      } else {
        this.dropdownPosition.left = this.cursorPosition.left
      }
    },

    async setDropdownPositionByClickInTextArea(e) {
      await this.$nextTick()
      const y = e.target.getBoundingClientRect().top - this.editor.getBoundingClientRect().top + this.cursorPosition.height
      const x = e.target.getBoundingClientRect().left - this.editor.getBoundingClientRect().left
      const dropdown = this.$refs.richInputLink
      this.editor.appendChild(dropdown)
      this.dropdownPosition.top = y
      if (dropdown.clientWidth < x) {
        this.dropdownPosition.left = x - dropdown.clientWidth + e.target.offsetWidth
      } else {
        this.dropdownPosition.left = x
      }
    }
  },

  async mounted() {
    await this.$nextTick()
    this.editor.addEventListener('click', this.onClick)
  },

  beforeDestroy() {
    this.editor.removeEventListener('click', this.onClick)
  }
}
</script>
